import { gql } from 'graphql-request'
import { useMutation } from '@/proxy/react-query'
import { client } from '@/proxy/client'

type GraphqlBusinessError = {
  type?: string
  code?: string
  title?: string
  messages?: string[]
}

type ZendeskMessagingJwtResponse = {
  zendeskMessagingJwt?: {
    token?: string
  }
  error?: GraphqlBusinessError
}

export const ZENDESK_MESSAGING_JWT = gql`
  mutation sellerZendeskMessagingJwt {
    zendeskMessagingJwt {
      token
    }
  }
`

const buildZendeskJwtErrorMessage = (error?: GraphqlBusinessError) => {
  if (!error) return 'Unable to generate Zendesk messaging JWT'

  const message = error.messages?.[0] ?? error.title ?? error.code
  return message || 'Unable to generate Zendesk messaging JWT'
}

const logZendeskJwtDebug = (
  message: string,
  details: Record<string, unknown>
) => {
  if (process.env.ENV !== 'production') {
    console.info(`[ZendeskJWT] ${message}`, details)
  }
}

export const useZendeskMessagingJwt = () =>
  useMutation({
    mutationFn: async () => {
      const response = await client.request<ZendeskMessagingJwtResponse>(
        ZENDESK_MESSAGING_JWT
      )

      logZendeskJwtDebug('response', {
        hasToken: Boolean(response.zendeskMessagingJwt?.token),
        hasError: Boolean(response.error),
        errorCode: response.error?.code ?? null,
        errorType: response.error?.type ?? null,
        errorTitle: response.error?.title ?? null,
      })

      if (response.error) {
        throw new Error(buildZendeskJwtErrorMessage(response.error))
      }

      const token = response.zendeskMessagingJwt?.token
      if (!token) {
        throw new Error('Zendesk JWT token was not returned by backend')
      }

      return token
    },
  })
